import React, { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";

import { getUsers } from "@/api/audience";
import { Audience, User } from "@/types";

export interface ShareDialogProps {
  selectedEntity: Audience;
  entityType: "Audience" | "Activation";
  open: boolean;
  onClose: () => void;
  onShare: (ids: number[]) => void;
}

const ShareDialog: React.FC<ShareDialogProps> = ({
  selectedEntity,
  entityType,
  open,
  onClose,
  onShare,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [sharedUsers, setSharedUsers] = React.useState<User[]>([]);
  const [allUsers, setAllUsers] = React.useState<User[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const accessToken = await getAccessTokenSilently();
      const { data, error } = await getUsers(accessToken);
      if (error) {
        console.error(error);
        setLoading(false);
        return;
      }
      if (data) {
        setLoading(false);
        setAllUsers(data);
        if (
          selectedEntity.shared_with &&
          selectedEntity.shared_with.length > 0
        ) {
          const currentSharedUsers = data.filter((user) =>
            selectedEntity.shared_with?.includes(user.id)
          );
          const deduped = Array.from(
            new Map(currentSharedUsers.map((user) => [user.id, user])).values()
          );
          setSharedUsers(deduped);
        } else {
          setSharedUsers([]);
        }
      }
    };
    fetchData();
  }, [getAccessTokenSilently, selectedEntity]);

  const handleShare = () => {
    const users = sharedUsers.map((user) => user.id);
    onShare(users);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Share {entityType}</DialogTitle>
      <DialogContent sx={{ width: 400 }}>
        {loading ? (
          <Skeleton variant="rounded" height={56} />
        ) : (
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={allUsers}
            groupBy={(option) => option.group}
            getOptionLabel={(option) => option.name}
            value={sharedUsers}
            onChange={(event, newValue) => {
              // Dedupe by ID
              const deduped = Array.from(
                new Map(newValue.map((user) => [user.id, user])).values()
              );

              setSharedUsers(deduped);
            }}
            // filterSelectedOptions
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.name}
                  label={option.name}
                  color="primary"
                  variant="outlined"
                  avatar={
                    <img
                      src={option.image}
                      alt={option.name}
                      style={{ width: 20, height: 20, borderRadius: "50%" }}
                    />
                  }
                />
              ))
            }
            renderOption={(props, option) => (
              <Box
                {...props}
                key={option.name}
                component="li"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <img
                  src={option.image}
                  alt={option.name}
                  style={{
                    width: 35,
                    height: 35,
                    borderRadius: "50%",
                    marginRight: 8,
                  }}
                />
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant={"body1"}>{option.name}</Typography>
                  <Typography variant={"body2"} color={"textSecondary"}>
                    {option.email}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                fullWidth
                placeholder="Select Users"
              />
            )}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={"contained"}
          size={"small"}
          disabled={loading}
          onClick={handleShare}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareDialog;
